import { parseJsonArray } from '../utils/parseJsonArray';

function normalizeReport(report) {
  if (!report || typeof report !== 'object') return report;
  return {
    ...report,
    photoCaptions: parseJsonArray(report.photoCaptions),
    photoUrls: parseJsonArray(report.photoUrls),
    photoApprovalStatus: parseJsonArray(report.photoApprovalStatus),
  };
}

function normalizeReportsResponse(data) {
  if (!data || typeof data !== 'object') return data;
  const next = { ...data };
  if (Array.isArray(next.reports)) {
    next.reports = next.reports.map(normalizeReport);
  }
  if (next.report) {
    next.report = normalizeReport(next.report);
  }
  return next;
}

// Get API URL from runtime configuration
function getApiBaseUrl() {
  // First try to get from window object (set by runtime environment)
  if (window.REACT_APP_API_URL) {
    return window.REACT_APP_API_URL;
  }

  // Fallback to build-time environment variable
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Final fallback
  return 'http://localhost:3001/api';
}

// Get Google Maps API Key from runtime configuration
function getGoogleMapsApiKey() {
  // First try to get from window object (set by runtime environment)
  if (window.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return window.REACT_APP_GOOGLE_MAPS_API_KEY;
  }

  // Fallback to build-time environment variable
  if (process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  }

  // No fallback - throw error
  throw new Error(
    'REACT_APP_GOOGLE_MAPS_API_KEY is required but not found in environment variables',
  );
}

const API_BASE_URL = getApiBaseUrl();

// Validate API configuration
if (!window.REACT_APP_API_URL && !process.env.REACT_APP_API_URL) {
  console.warn('⚠️  REACT_APP_API_URL not set. Using default localhost:3001.');
  console.warn(
    '💡 To configure for different servers, set window.REACT_APP_API_URL or create a .env file with:',
  );
  console.warn('   REACT_APP_API_URL=http://YOUR_SERVER_IP:3001/api');
  console.warn('   Update this to match SERVER_HOST in backend/.env');
}

// Validate Google Maps API Key
try {
  getGoogleMapsApiKey();
} catch (error) {
  console.error('❌ Google Maps API Key validation failed:', error.message);
  console.error('💡 Please set REACT_APP_GOOGLE_MAPS_API_KEY in your environment variables');
}

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = localStorage.getItem('swarm_token');
    this.retryDelays = [1000, 2000, 5000]; // Retry delays in milliseconds
    this.maxRetries = 3;

    // Log the configured API URL for debugging
    console.log(`🔗 API Service configured for: ${this.baseURL}`);
  }

  // Set authentication token
  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('swarm_token', token);
    } else {
      localStorage.removeItem('swarm_token');
    }
  }

  // Get authentication headers
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
      console.log('🔑 Using token for request:', `${this.token.substring(0, 20)}...`);
    } else {
      console.log('⚠️ No token available for request');
    }

    return headers;
  }

  // Get current token
  getToken() {
    return this.token;
  }

  // Generic request method with retry logic
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: options.skipAuth ? { 'Content-Type': 'application/json' } : this.getHeaders(),
      ...options,
    };

    let lastError;

    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        const response = await fetch(url, config);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;

          // Check if it's a rate limit error
          if (response.status === 429 || errorMessage.toLowerCase().includes('too many requests')) {
            if (attempt < this.maxRetries) {
              const delay =
                this.retryDelays[attempt] || this.retryDelays[this.retryDelays.length - 1];
              console.log(
                `Rate limited. Retrying in ${delay}ms (attempt ${attempt + 1}/${this.maxRetries + 1})`,
              );
              await new Promise(resolve => setTimeout(resolve, delay));
              continue;
            } else {
              throw new Error('Rate limit exceeded. Please try again later.');
            }
          }

          throw new Error(errorMessage);
        }

        return await response.json();
      } catch (error) {
        lastError = error;

        // If it's not a rate limit error or we've exhausted retries, throw immediately
        if (
          !error.message.toLowerCase().includes('too many requests') ||
          attempt >= this.maxRetries
        ) {
          break;
        }

        // For other network errors, retry with exponential backoff
        if (attempt < this.maxRetries) {
          const delay = this.retryDelays[attempt] || this.retryDelays[this.retryDelays.length - 1];
          console.log(
            `Request failed. Retrying in ${delay}ms (attempt ${attempt + 1}/${this.maxRetries + 1})`,
          );
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    console.error('API request failed after all retries:', lastError);
    throw lastError;
  }

  // Generic HTTP methods
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  // Authentication endpoints
  async register(userData) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async login(credentials) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async getProfile() {
    return this.request('/auth/profile');
  }

  async updateProfile(profileData) {
    return this.request('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  async verifyToken() {
    return this.request('/auth/verify');
  }

  async forgotPassword(email) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async verifyResetToken(token) {
    return this.request(`/auth/reset-password/verify/${token}`);
  }

  async resetPassword(token, password) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  // 2FA endpoints
  async setup2FA() {
    return this.request('/auth/setup-2fa', {
      method: 'POST',
    });
  }

  async enable2FA(secret, token, backupCodes) {
    return this.request('/auth/enable-2fa', {
      method: 'POST',
      body: JSON.stringify({ secret, token, backupCodes }),
    });
  }

  async disable2FA(token) {
    return this.request('/auth/disable-2fa', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async verify2FA(token, tempToken) {
    return this.request('/auth/verify-2fa', {
      method: 'POST',
      body: JSON.stringify({ token, tempToken }),
    });
  }

  // Reports endpoints
  async getReports() {
    // Use public endpoint if no token is available
    const token = this.getToken();
    const data = !token
      ? await this.request('/reports/public?limit=100', { skipAuth: true })
      : await this.request('/reports?limit=100');
    return normalizeReportsResponse(data);
  }

  async getReport(id) {
    return normalizeReportsResponse(await this.request(`/reports/${id}`));
  }

  async createReport(reportData) {
    return normalizeReportsResponse(
      await this.request('/reports', {
        method: 'POST',
        body: JSON.stringify(reportData),
      }),
    );
  }

  async updateReport(id, reportData) {
    return normalizeReportsResponse(
      await this.request(`/reports/${id}`, {
        method: 'PUT',
        body: JSON.stringify(reportData),
      }),
    );
  }

  async deleteReport(id) {
    return this.request(`/reports/${id}`, {
      method: 'DELETE',
    });
  }

  async getPendingReports() {
    return normalizeReportsResponse(await this.request('/reports/pending'));
  }

  async approveReport(id) {
    return this.request(`/reports/${id}/approve`, {
      method: 'POST',
    });
  }

  async rejectReport(id) {
    return this.request(`/reports/${id}/reject`, {
      method: 'POST',
    });
  }

  async revokeApproval(id) {
    return this.request(`/reports/${id}/revoke-approval`, {
      method: 'POST',
    });
  }

  async restoreReport(id) {
    return this.request(`/reports/${id}/restore`, {
      method: 'POST',
    });
  }

  async approvePhoto(reportId, photoIndex) {
    return this.request(`/reports/${reportId}/photos/${photoIndex}/approve`, {
      method: 'PUT',
    });
  }

  async rejectPhoto(reportId, photoIndex) {
    return this.request(`/reports/${reportId}/photos/${photoIndex}/reject`, {
      method: 'PUT',
    });
  }

  async importReports(reports, importAsApproved = false) {
    return this.request('/reports/import', {
      method: 'POST',
      body: JSON.stringify({ reports, importAsApproved }),
    });
  }

  async exportReports(includeImages = false) {
    const endpoint = includeImages ? '/reports/export/images' : '/reports/export/csv';
    const filename = includeImages ? 'swarm_reports_with_images.zip' : 'swarm_reports.csv';

    const url = `${this.baseURL}${endpoint}`;
    const response = await fetch(url, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Users endpoints (admin only)
  async getUsers() {
    return this.request('/users');
  }

  async getUser(email) {
    return this.request(`/users/${email}`);
  }

  async updateUser(email, userData) {
    return this.request(`/users/${email}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(email) {
    return this.request(`/users/${email}`, {
      method: 'DELETE',
    });
  }

  async toggleAdmin(email) {
    return this.request(`/users/${email}/toggle-admin`, {
      method: 'PATCH',
    });
  }

  async toggle2FA(email) {
    return this.request(`/users/${email}/toggle-2fa`, {
      method: 'PATCH',
    });
  }

  async resetUserPassword(email, newPassword) {
    return this.request(`/users/${email}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    });
  }

  // Alert endpoints (admin only)
  async getAlertRecipients() {
    return this.request('/users/alerts/recipients');
  }

  async saveAlertRecipients(recipients) {
    return this.request('/users/alerts/recipients', {
      method: 'POST',
      body: JSON.stringify(recipients),
    });
  }

  async getUserAlertPreferences() {
    return this.request('/users/alerts/preferences');
  }

  async saveUserAlertPreferences(preferences) {
    return this.request('/users/alerts/preferences', {
      method: 'POST',
      body: JSON.stringify({ preferences }),
    });
  }

  async toggleUserAlertPreference(userEmail, alertType) {
    console.log('🔄 Toggling user alert preference:', { userEmail, alertType });
    return this.request('/users/alerts/preferences/toggle', {
      method: 'POST',
      body: JSON.stringify({ userEmail, alertType }),
    });
  }

  async getUsersForAlertType(alertType) {
    return this.request(`/users/alerts/users/${alertType}`);
  }

  // File upload endpoints
  async uploadPhotos(files, onProgress = null) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('photos', file);
    });

    const url = `${this.baseURL}/uploads/photos`;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Set up progress tracking
      if (onProgress) {
        xhr.upload.addEventListener('progress', event => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            onProgress(percentComplete);
          }
        });
      }

      // Set up response handling
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (error) {
            reject(new Error('Invalid JSON response from server'));
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            reject(new Error(errorData.error || `HTTP error! status: ${xhr.status}`));
          } catch (error) {
            reject(new Error(`HTTP error! status: ${xhr.status}`));
          }
        }
      });

      // Set up error handling
      xhr.addEventListener('error', () => {
        reject(new Error('Network error occurred during upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload was aborted'));
      });

      // Open and send the request
      xhr.open('POST', url);

      // Set headers
      if (this.token) {
        xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      }

      xhr.send(formData);
    });
  }

  async deleteFile(filename) {
    return this.request(`/uploads/file/${filename}`, {
      method: 'DELETE',
    });
  }

  // Get file URL
  getFileUrl(filename) {
    return `${this.baseURL.replace('/api', '')}/uploads/${filename}`;
  }

  // Get photo metadata including aspect ratio
  async getPhotoMetadata(filename) {
    return this.request(`/uploads/metadata/${filename}`);
  }

  // MailerLite endpoints
  // Generic Email Service methods (works with any configured provider)
  async validateEmailConnection() {
    return this.request('/email/status');
  }

  async getEmailGroups() {
    return this.request('/email/groups');
  }

  async getEmailGroupSubscribers(groupId, limit = 100, offset = 0) {
    return this.request(`/email/groups/${groupId}/subscribers?limit=${limit}&offset=${offset}`);
  }

  async addEmailSubscriber(groupId, subscriberData) {
    return this.request(`/email/groups/${groupId}/subscribers`, {
      method: 'POST',
      body: JSON.stringify(subscriberData),
    });
  }

  async removeEmailSubscriber(groupId, email) {
    return this.request(`/email/groups/${groupId}/subscribers/${email}`, {
      method: 'DELETE',
    });
  }

  async subscribeToSWARMUpdates(subscriberData) {
    // This would need to be configured with a specific group ID
    const defaultGroupId = process.env.DEFAULT_EMAIL_GROUP_ID || '1';
    return this.request(`/email/groups/${defaultGroupId}/subscribers`, {
      method: 'POST',
      body: JSON.stringify(subscriberData),
    });
  }

  async unsubscribeFromSWARMUpdates(email) {
    const defaultGroupId = process.env.DEFAULT_EMAIL_GROUP_ID || '1';
    return this.request(`/email/groups/${defaultGroupId}/subscribers/${email}`, {
      method: 'DELETE',
    });
  }

  async sendIncidentAlert(incidentData) {
    return this.request('/email/alerts/incident', {
      method: 'POST',
      body: JSON.stringify(incidentData),
    });
  }

  async createEmailCampaign(campaignData) {
    return this.request('/email/campaigns', {
      method: 'POST',
      body: JSON.stringify(campaignData),
    });
  }

  async sendEmailCampaign(campaignId) {
    return this.request(`/email/campaigns/${campaignId}/send`, {
      method: 'POST',
    });
  }

  async getEmailStats() {
    return this.request('/email/stats');
  }

  // Configuration endpoints (super admin only)
  async getAppConfig() {
    return this.request('/config/app-config');
  }

  async updateAppConfig(config) {
    return this.request('/config/app-config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async getSystemStats() {
    return this.request('/config/system-stats');
  }

  async getUserManagement() {
    return this.request('/config/user-management');
  }

  async toggleSuperAdmin(email) {
    return this.request(`/config/toggle-super-admin/${email}`, {
      method: 'POST',
    });
  }

  // Area configuration endpoints
  async getAreaConfig() {
    return this.request('/config/area-config');
  }

  async createAreaConfig(areaData) {
    return this.request('/config/area-config', {
      method: 'POST',
      body: JSON.stringify(areaData),
    });
  }

  async updateAreaConfig(id, areaData) {
    return this.request(`/config/area-config/${id}`, {
      method: 'PUT',
      body: JSON.stringify(areaData),
    });
  }

  async deleteAreaConfig(id) {
    return this.request(`/config/area-config/${id}`, {
      method: 'DELETE',
    });
  }

  // Branding configuration endpoints
  async getBrandingConfig() {
    return this.request('/config/branding-config');
  }

  async updateBrandingConfig(brandingData) {
    return this.request('/config/branding-config', {
      method: 'PUT',
      body: JSON.stringify(brandingData),
    });
  }

  // Upload configuration endpoints
  async getUploadConfig() {
    return this.request('/config/upload-config');
  }

  async updateUploadConfig(config) {
    return this.request('/config/upload-config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async getHeatmapConfig() {
    return this.request('/config/heatmap-config');
  }

  async updateHeatmapConfig(config) {
    return this.request('/config/heatmap-config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  // Extra Notes endpoints
  async getExtraNotes(reportId) {
    return this.request(`/extra-notes/report/${reportId}`);
  }

  async getPublicExtraNotes(reportId) {
    return this.request(`/extra-notes/report/${reportId}/public`);
  }

  async getPendingExtraNotes() {
    return this.request('/extra-notes/pending');
  }

  async createExtraNote(noteData) {
    return this.request('/extra-notes', {
      method: 'POST',
      body: JSON.stringify(noteData),
    });
  }

  async createAnonymousExtraNote(noteData) {
    return this.request('/extra-notes/anonymous', {
      method: 'POST',
      body: JSON.stringify(noteData),
    });
  }

  async updateExtraNote(noteId, noteData) {
    return this.request(`/extra-notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(noteData),
    });
  }

  async approveExtraNote(noteId) {
    return this.request(`/extra-notes/${noteId}/approve`, {
      method: 'POST',
    });
  }

  async rejectExtraNote(noteId) {
    return this.request(`/extra-notes/${noteId}/reject`, {
      method: 'POST',
    });
  }

  async unpublishExtraNote(noteId) {
    return this.request(`/extra-notes/${noteId}/unpublish`, {
      method: 'POST',
    });
  }

  async deleteExtraNote(noteId) {
    return this.request(`/extra-notes/${noteId}`, {
      method: 'DELETE',
    });
  }

  // Database management endpoints (super admin only)
  async getDatabaseStats() {
    return this.request('/admin/database/stats');
  }

  async clearAllReports() {
    return this.request('/admin/database/clear-reports', {
      method: 'POST',
    });
  }

  async clearUnapprovedReports() {
    return this.request('/admin/database/clear-unapproved-reports', {
      method: 'POST',
    });
  }

  async clearApprovedReports() {
    return this.request('/admin/database/clear-approved-reports', {
      method: 'POST',
    });
  }

  async resetAutoIncrement() {
    return this.request('/admin/database/reset-auto-increment', {
      method: 'POST',
    });
  }

  async populateSampleData() {
    return this.request('/admin/database/populate-sample-data', {
      method: 'POST',
    });
  }

  async optimizeDatabase() {
    return this.request('/admin/database/vacuum-database', {
      method: 'POST',
    });
  }

  async getTableStatus() {
    return this.request('/admin/database/table-status');
  }

  async syncTables(tablesToSync = null) {
    return this.request('/admin/database/sync-tables', {
      method: 'POST',
      body: JSON.stringify({ tablesToSync }),
    });
  }

  async backupDatabase(format = 'json') {
    try {
      const response = await fetch(`${this.baseURL}/admin/database/backup?format=${format}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Extract filename from response headers
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename =
        format === 'sql' ? 'swarm-reporter-backup.sql.zip' : 'swarm-reporter-backup.json';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      if (format === 'sql') {
        // Handle ZIP file download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return {
          success: true,
          message: 'SQL database backup downloaded successfully',
        };
      } else {
        // Handle JSON file download
        const backupData = await response.json();
        const blob = new Blob([JSON.stringify(backupData, null, 2)], {
          type: 'application/json',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return {
          success: true,
          message: 'JSON database backup downloaded successfully',
          data: backupData,
        };
      }
    } catch (error) {
      console.error('Backup error:', error);
      throw error;
    }
  }

  async restoreDatabase(file) {
    const formData = new FormData();
    formData.append('backupFile', file);

    return this.request('/admin/database/restore', {
      method: 'POST',
      body: formData,
      // Don't set Content-Type header, let browser set it with boundary for FormData
      headers: {
        Authorization: `Bearer ${this.getToken()}`,
      },
    });
  }

  // Analytics API methods
  async getVisitorStats(timeRange = 'day', startDate = null, endDate = null) {
    const params = new URLSearchParams({ timeRange });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    return this.get(`/config/analytics/visitor-stats?${params}`);
  }

  async getGeographicData(timeRange = 'day', startDate = null, endDate = null) {
    const params = new URLSearchParams({ timeRange });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    return this.get(`/config/analytics/geographic?${params}`);
  }

  async getTrafficTimeline(timeRange = 'day', startDate = null, endDate = null) {
    const params = new URLSearchParams({ timeRange });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    return this.get(`/config/analytics/traffic-timeline?${params}`);
  }

  async getPopularPages(timeRange = 'day', startDate = null, endDate = null) {
    const params = new URLSearchParams({ timeRange });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    return this.get(`/config/analytics/popular-pages?${params}`);
  }

  async getUserAgentStats(timeRange = 'day', startDate = null, endDate = null) {
    const params = new URLSearchParams({ timeRange });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    return this.get(`/config/analytics/user-agents?${params}`);
  }
}

const apiService = new ApiService();
export default apiService;
